import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  makeStyles,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { EmailUser, RegisterConnectUser } from 'hydra-data-types';
import { FunctionComponent, useEffect, useState } from 'react';
import PhoneInputWithCountrySelect, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';
import {
  makeFormErrors,
  registerExistingUser,
  registerHcp,
  registerPatient,
} from '../../api/AuthApi';
import buttonArrow from '../../assets/svgs/arrow-right.svg';
import { AlertBar } from '../../components/AlertBar';
import { CONNECT_SCREENS, USER_TYPE } from '../../constants';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { isNonEmptyString } from '../../helpers/StringUtils';
import { useAuthDispatch, useAuthStore } from '../../store/AuthStore';
import { ActionType } from '../../store/StoreTypes';
import { colors } from '../../theme/colors';
import { ValidationMessages } from './types';

const hcpUrls = {
  termsOfService: 'https://www.mindsethealth.com/terms-of-service',
  privacyPolicy: 'https://www.mindsethealth.com/privacy-policy',
  websiteDataProtectionPolicy: 'https://www.mindsethealth.com/legal/website-data-protection-policy',
};
const patientUrls = {
  termsOfService: 'https://www.mindsethealth.com/legal/terms-conditions-nerva-app',
  privacyPolicy: 'https://www.mindsethealth.com/legal/nerva-privacy-policy',
  websiteDataProtectionPolicy: 'https://www.mindsethealth.com/legal/website-data-protection-policy',
};

type RegisterHcpUserWithTerms = {
  checked: boolean;
} & RegisterConnectUser;

const useStyles = makeStyles(() => ({
  root: {
    maxWidth: 'inherit',
  },
}));

type TermsAndConditionsProps = {
  hcpReferralHash: string;
};

const PhoneInput = styled(PhoneInputWithCountrySelect)`
  .PhoneInputInput {
    border: 0px;
    background-color: transparant;
    &:focus {
      outline: none;
    }
  }
  gap: 10px;
  border-bottom: 1px solid #00000080;
  &:focus-within {
    border-bottom: 2px solid;
    border-bottom-color: ${colors.green};
  }
`;

const TermsAndConditions: FunctionComponent<TermsAndConditionsProps> = (props) => {
  const userIsPatient = isNonEmptyString(props.hcpReferralHash);
  const urls = userIsPatient ? patientUrls : hcpUrls;

  return (
    <>
      <Typography variant="caption">I accept the </Typography>
      <Link variant="caption" color="primary" href={urls.termsOfService} target="_blank">
        Terms
      </Link>
      <Typography variant="caption">, </Typography>
      <Link variant="caption" color="primary" href={urls.privacyPolicy} target="_blank">
        Privacy
      </Link>
      <Typography variant="caption"> &amp; </Typography>
      <Link
        variant="caption"
        color="primary"
        href={urls.websiteDataProtectionPolicy}
        target="_blank"
      >
        Data collection policy
      </Link>
    </>
  );
};

type RegisterScreenProps = {
  email: string;
  password: string;
};

export const RegisterScreen: FunctionComponent<RegisterScreenProps> = (props) => {
  const { email, password } = props;

  const hcpReferralHash = useAuthStore((state) => state.hcpReferralHash);
  const hasHcpReferralHash = isNonEmptyString(hcpReferralHash);
  const [errorMessage, setErrorMessage] = useState<string>();
  const classes = useStyles();
  const dispatch = useAuthDispatch();
  const history = useHistory();

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.REGISTER,
    });
  }, []);

  const initialValues: RegisterHcpUserWithTerms = {
    email,
    phoneNumber: '',
    name: '',
    lastName: '',
    password,
    profession: '',
    checked: false,
    clinicName: '',
    attribution: '',
  };

  const {
    EMAIL_REQUIRED,
    FIRST_NAME_REQUIRED,
    INVALID_EMAIL,
    LAST_NAME_REQUIRED,
    PASSWORD_REQUIRED,
    PASSWORD_REQUIREMENT,
    PROFESSION_REQUIRED,
    PHONE_NUMBER_INVALID,
  } = ValidationMessages;

  const SCHEMA: yup.ObjectSchema<RegisterConnectUser> = yup
    .object()
    .shape({
      email: yup.string().email(INVALID_EMAIL).required(EMAIL_REQUIRED),
      name: yup.string().required(FIRST_NAME_REQUIRED),
      lastName: yup.string().required(LAST_NAME_REQUIRED),
      profession: yup.lazy(() => {
        return !hasHcpReferralHash
          ? yup.string().required(PROFESSION_REQUIRED)
          : yup.string().notRequired();
      }),
      password: yup.lazy((value) => {
        if (!isNonEmptyString(password)) {
          if (value !== undefined) {
            return yup
              .string()
              .matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{6,}$/, PASSWORD_REQUIREMENT);
          }
          return yup.string().required(PASSWORD_REQUIRED);
        }
        return yup.string().notRequired();
      }),
      phoneNumber: yup.string().test(
        'is-valid',
        PHONE_NUMBER_INVALID,
        // Only validate if a phone number was entered
        (value) => (isNonEmptyString(value) ? isValidPhoneNumber(value ?? '') : true)
      ),
    })
    .required();

  async function onSubmit(
    values: RegisterHcpUserWithTerms,
    helpers: FormikHelpers<RegisterHcpUserWithTerms>
  ) {
    if (values.checked) {
      try {
        let user: EmailUser;
        if (isNonEmptyString(email) && isNonEmptyString(password)) {
          user = await registerExistingUser({
            lastName: values.lastName,
            name: values.name,
            profession: values.profession,
            password: values.password,
            clinicName: values.clinicName,
            attribution: values.attribution,
            phoneNumber: values.phoneNumber,
          });
        } else if (hasHcpReferralHash) {
          user = await registerPatient(values);
          Analytics.track({
            event: AnalyticsEventName.a_signup_succeeded,
            userType: USER_TYPE.PATIENT,
          });
        } else {
          user = await registerHcp(values);
          Analytics.track({
            event: AnalyticsEventName.a_signup_succeeded,
            userType: USER_TYPE.HCP,
            attribution: values.attribution,
          });
        }

        dispatch({
          type: ActionType.SET_AUTHENTICATING_EXISTING_MINDSET_ID,
          authenticatingExistingMindsetId: false,
        });
        dispatch({ type: ActionType.SET_USER, user });

        if (hasHcpReferralHash) {
          // await createHcpPatientLink({
          //   hcpId: hcpReferralHash,
          //   patientId: user.userId,
          //   patientProduct: Product.Nerva,
          //   patientConsent: false
          // } as ConnectLink);

          history.push('/users/patient/consent');
        } else {
          history.push('/');
        }
      } catch (error) {
        handleErrors(error, helpers);
      }
    } else {
      setErrorMessage('You need to accept the terms, privacy policy and data collection policy.');
    }
  }

  function handleErrors(error: any, { setErrors }: FormikHelpers<RegisterHcpUserWithTerms>) {
    const formErrors = makeFormErrors(error);
    if (formErrors !== undefined) {
      setErrors(formErrors);
    } else {
      setErrorMessage('Unable to sign up, please try again');
      Analytics.track({
        event: Analytics.Event.a_signup_failed,
        error,
        userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
      });
    }
  }

  return (
    <Grid
      container
      direction="column"
      data-testid="register-screen"
      classes={{ root: classes.root }}
    >
      <AlertBar
        copy={errorMessage}
        status="warning"
        open={isNonEmptyString(errorMessage)}
        onClose={(isOpen) => !isOpen && setErrorMessage(undefined)}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={SCHEMA}
        onSubmit={onSubmit}
        validateOnBlur={true}
      >
        {(formikProps: FormikProps<RegisterHcpUserWithTerms>) => {
          const {
            values,
            touched,
            dirty,
            errors,
            handleBlur,
            handleChange,
            isSubmitting,
            setFieldValue,
          } = formikProps;

          return (
            <Form>
              <Grid container item direction="column" spacing={3} classes={{ root: classes.root }}>
                <Grid item>
                  <TextField
                    required
                    fullWidth
                    name="name"
                    label="First name"
                    value={values.name}
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ 'data-testid': 'First name' }}
                    helperText={isNonEmptyString(errors.name) && touched.name && errors.name}
                    error={!!(isNonEmptyString(errors.name) && touched.name)}
                  />
                </Grid>

                <Grid item>
                  <TextField
                    required
                    fullWidth
                    name="lastName"
                    label="Last name"
                    value={values.lastName}
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ 'data-testid': 'Last name' }}
                    helperText={
                      isNonEmptyString(errors.lastName) && touched.lastName && errors.lastName
                    }
                    error={!!(isNonEmptyString(errors.lastName) && touched.lastName)}
                  />
                </Grid>

                {!hasHcpReferralHash && (
                  <Grid item>
                    <FormControl
                      fullWidth
                      variant="standard"
                      required
                      error={!!(isNonEmptyString(errors.profession) && touched.profession)}
                    >
                      <InputLabel id="profession-label">Profession</InputLabel>
                      <Select
                        label="Profession"
                        labelId="profession-label"
                        name="profession"
                        value={values.profession}
                        autoWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputProps={{ 'data-testid': 'Profession' }}
                      >
                        <MenuItem value={'General Practitioner'}>
                          General Practitioner / Primary Care Physician
                        </MenuItem>
                        <MenuItem value={'Dietician'}>Dietitian</MenuItem>
                        <MenuItem value={'Nutritionist'}>Nutritionist</MenuItem>
                        <MenuItem value={'Gastroenterologist'}>Gastroenterologist</MenuItem>
                        <MenuItem value={'Naturopathic Doctor'}>Naturopathic Doctor</MenuItem>
                        <MenuItem value={'Naturopath'}>Naturopath</MenuItem>
                        <MenuItem value={'Psychologist'}>Psychologist</MenuItem>
                        <MenuItem value={'Nurse'}>Nurse</MenuItem>
                        <MenuItem value={'Other'}>Other</MenuItem>
                      </Select>
                      <FormHelperText>
                        {isNonEmptyString(errors.profession) &&
                          touched.profession &&
                          errors.profession}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}

                {!hasHcpReferralHash && (
                  <Grid item>
                    <TextField
                      fullWidth
                      name="clinicName"
                      value={values.clinicName}
                      type="text"
                      inputProps={{ 'data-testid': 'Clinic name' }}
                      label="Clinic name"
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Grid>
                )}

                {!hasHcpReferralHash && (
                  <Grid item>
                    <FormControl fullWidth variant="standard">
                      <InputLabel id="attribution-label">How did you hear about us?</InputLabel>
                      <Select
                        label="How did you hear about us?"
                        labelId="attribution-label"
                        name="attribution"
                        value={values.attribution}
                        autoWidth
                        onChange={handleChange}
                        onBlur={handleBlur}
                        inputProps={{ 'data-testid': 'Attribution' }}
                      >
                        <MenuItem value={'colleague'}>
                          Colleague / other healthcare practitioner
                        </MenuItem>
                        <MenuItem value={'physicalLetter'}>
                          Received a physical letter about a patient
                        </MenuItem>
                        <MenuItem value={'conference'}>Spoke to someone at a conference</MenuItem>
                        <MenuItem value={'fromPatient'}>Patient told me</MenuItem>
                        <MenuItem value={'newsArticle'}>News article or blog post</MenuItem>
                        <MenuItem value={'nervaEmail'}>Email from Nerva</MenuItem>
                        <MenuItem value={'socialMedia'}>
                          Facebook / Instagram advertisement
                        </MenuItem>
                        <MenuItem value={'searchEngine'}>
                          Searching for information on search engines e.g. Google
                        </MenuItem>
                        <MenuItem value={'completedProgram'}>
                          Completed one of our programs
                        </MenuItem>
                        <MenuItem value={'ausDoc'}>AusDoc</MenuItem>
                        <MenuItem value={'other'}>Other</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                {!hasHcpReferralHash && (
                  <Grid item>
                    <PhoneInput
                      placeholder="Phone Number"
                      international
                      countryCallingCodeEditable={false}
                      value={values.phoneNumber?.toString()}
                      defaultCountry="US"
                      data-testid="Phone number"
                      name="phoneNumber"
                      onBlur={handleBlur}
                      numberInputProps={{
                        style: {
                          height: '40px',
                          fontSize: '18px',
                          fontFamily: 'CircularXX-Light',
                        },
                      }}
                      onChange={(e) => setFieldValue('phoneNumber', e)}
                    />
                    {isNonEmptyString(errors.phoneNumber) && touched.phoneNumber && (
                      <FormHelperText error={true}>{errors.phoneNumber}</FormHelperText>
                    )}
                  </Grid>
                )}

                <Grid item>
                  <TextField
                    required
                    fullWidth
                    name="email"
                    label="Email"
                    type="email"
                    value={values.email ?? email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ 'data-testid': 'Email' }}
                    helperText={isNonEmptyString(errors.email) && touched.email && errors.email}
                    error={!!(isNonEmptyString(errors.email) && touched.email)}
                    disabled={Boolean(email)}
                  />
                </Grid>

                {!isNonEmptyString(password) && (
                  <Grid item>
                    <TextField
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      value={values.password ?? password}
                      type="password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={Boolean(password)}
                      inputProps={{ 'data-testid': 'Password' }}
                      helperText={
                        isNonEmptyString(errors.password) && touched.password && errors.password
                      }
                      error={!!(isNonEmptyString(errors.password) && touched.password && !password)}
                    />
                  </Grid>
                )}

                <Grid container item zeroMinWidth>
                  <Grid container item justifyContent="center">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.checked}
                          onChange={() => setFieldValue('checked', !values.checked)}
                          name="checked"
                          type="checkbox"
                          color="primary"
                        />
                      }
                      label={<TermsAndConditions hcpReferralHash={hcpReferralHash ?? ''} />}
                    />
                  </Grid>

                  <Grid container item justifyContent="center">
                    <Box width="270px" display="flex" position="relative" marginTop={2}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="small"
                        fullWidth
                        disabled={!dirty || isSubmitting}
                        endIcon={
                          <img src={buttonArrow} alt="next button" width="24px" height="24px" />
                        }
                      >
                        Create account
                        <Box position="absolute" bottom="0">
                          {isSubmitting && <CircularProgress size={24} color="primary" />}
                        </Box>
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </Grid>
  );
};
